import type { ReactNode } from "react";
import type { LucideIcon } from "lucide-react";
import type { GroomingStage } from "@ttc-spectree/toolbox-contract";
import type { GroomingSize } from "@/lib/api/grooming";
import { STAGE_LABELS, STAGE_TONES } from "@/lib/grooming/plain-words";
import { cn } from "@/lib/utils";

/**
 * Small pieces the grooming pages share (epic PI-SPEC-SW-E279): the chip
 * tones of the Final PM design (ok green, warn amber, cur blue, red, none
 * grey), the stage chip, the size chip and the three-step progress bar.
 */

export type ChipTone = "ok" | "warn" | "cur" | "red" | "none";

const TONE_CLASSES: Record<ChipTone, string> = {
  ok: "bg-green-500/[0.12] text-green-400",
  warn: "bg-amber-500/[0.12] text-amber-400",
  cur: "bg-link/[0.12] text-sky-300",
  red: "bg-primary/[0.12] text-red-300",
  none: "bg-secondary text-muted-foreground",
};

export function Chip({
  tone,
  children,
  className,
  testId,
  dataState,
}: {
  tone: ChipTone;
  children: ReactNode;
  className?: string;
  testId?: string;
  /** Rendered as `data-state`, for chips that name a state (BriefStateChip). */
  dataState?: string;
}) {
  return (
    <span
      className={cn(
        "inline-flex h-5 items-center gap-1 whitespace-nowrap rounded-full px-2 text-[11px] font-medium",
        TONE_CLASSES[tone],
        className
      )}
      data-testid={testId}
      data-state={dataState}
    >
      {children}
    </span>
  );
}

export function StageChip({ stage }: { stage: GroomingStage }) {
  return (
    <Chip tone={STAGE_TONES[stage]} testId="stage-chip">
      {STAGE_LABELS[stage]}
    </Chip>
  );
}

/**
 * The size of an item. Team points are solid ("3 pts"); an AI suggestion is
 * dashed and labelled AI ("AI 5"), so nobody reads it as the team's
 * estimate; an item with neither says "No size".
 */
export function SizeChip({ size }: { size: GroomingSize | null }) {
  const base = "inline-flex h-5 items-center whitespace-nowrap rounded-md px-1.5 text-[11px] font-semibold";
  if (!size) {
    return (
      <span className={cn(base, "text-muted-foreground")} data-testid="size-chip" data-size-source="none">
        No size
      </span>
    );
  }
  if (size.source === "ai") {
    return (
      <span
        className={cn(base, "border border-dashed border-zinc-600 text-zinc-300")}
        data-testid="size-chip"
        data-size-source="ai"
        title={`AI suggestion, not the team's estimate. Could be ${String(size.low)} to ${String(size.high)}.`}
      >
        AI {size.points}
      </span>
    );
  }
  return (
    <span className={cn(base, "bg-secondary text-foreground")} data-testid="size-chip" data-size-source="team">
      {size.points} pts
    </span>
  );
}

type StepState = "done" | "now" | "bad" | "todo";

const STEP_CLASSES: Record<StepState, string> = {
  done: "bg-green-500",
  now: "bg-link",
  bad: "bg-amber-500",
  todo: "bg-border",
};

function stepsOf(stage: GroomingStage): [StepState, StepState, StepState] {
  if (stage === "groomed") return ["done", "done", "done"];
  if (stage === "ready-to-groom") return ["done", "now", "todo"];
  return ["bad", "todo", "todo"];
}

/** Three steps: information, discussed, sized. */
export function ProgressSteps({ stage }: { stage: GroomingStage }) {
  const steps = stepsOf(stage);
  const names = ["Information", "Discussed", "Sized"];
  return (
    <div className="flex items-center gap-1" data-testid="progress-steps" aria-hidden="true">
      {steps.map((state, index) => (
        <span
          key={names[index]}
          className={cn("h-1.5 flex-1 rounded-full", STEP_CLASSES[state])}
          data-step-state={state}
        />
      ))}
    </div>
  );
}

/**
 * A segmented toggle (List / Stages, Top 30 / All). An option's optional
 * `icon` shows before its label (the Project page's tabs, PKG-PM-F).
 */
export function Segmented<T extends string>({
  label,
  value,
  options,
  onChange,
  disabled = false,
}: {
  label: string;
  value: T;
  options: readonly { value: T; label: string; icon?: LucideIcon }[];
  onChange: (value: T) => void;
  /** Every option off, such as while the backlog cannot be read (B6). */
  disabled?: boolean;
}) {
  return (
    <div
      role="group"
      aria-label={label}
      className={cn(
        "inline-flex gap-0.5 rounded-[9px] border border-border bg-[#111113] p-[3px]",
        disabled && "opacity-45"
      )}
    >
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          aria-pressed={option.value === value}
          disabled={disabled}
          onClick={() => {
            onChange(option.value);
          }}
          className={cn(
            "rounded-md px-3 py-1 text-sm font-medium transition-colors disabled:cursor-default",
            option.icon && "inline-flex items-center gap-1.5",
            option.value === value ? "bg-border text-foreground" : "text-muted-foreground enabled:hover:text-foreground"
          )}
        >
          {option.icon && <option.icon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />}
          {option.label}
        </button>
      ))}
    </div>
  );
}
